import { useEffect, useRef } from "react";
import { useAssessment } from "@/contexts/AssessmentContext/AssessmentContext";
import { useSessionId } from "@/contexts/SessionContext/SessionContext";
import { useApi } from "@/contexts/ApiContext/ApiContext";
import { PageState } from "@/contexts/AssessmentContext/AssessmentContext.types";
import { getMarketingConsent } from "@/utils/marketingConsent";

/**
 * Hook that tracks page views as the user navigates through the assessment.
 * Fire-and-forget POST to /analytics on each page transition to track analytics, including page-views
 *
 * Automatically derives pageId from:
 * - The current question's questionId (for questionnaire screens)
 * - "polling" (for the polling screen)
 * - "results" (for the results screen)
 */
export function usePageViewTracking(): void {
  const { currentQuestion, currentIndex, pageState } = useAssessment();
  const { sessionId } = useSessionId();
  const api = useApi();
  const lastTrackedRef = useRef<string | null>(null);

  useEffect(() => {
    let pageId: string | null = null;

    if (pageState === PageState.QUESTIONNAIRE) {
      pageId = currentQuestion?.questionId ?? null;
    } else if (pageState === PageState.POLLING) {
      pageId = "polling";
    } else if (pageState === PageState.ERROR) {
      pageId = "error";
    } else if (pageState === PageState.RESULTS) {
      pageId = "results";
    }

    if (!pageId || !sessionId) return;
    const trackingKey = `${sessionId}:${pageId}`;
    if (trackingKey === lastTrackedRef.current) return;

    lastTrackedRef.current = trackingKey;

    api
      .postPageView({
        sessionId: sessionId,
        pageId: pageId,
        marketingConsent: getMarketingConsent(),
      })
      .catch(() => {});
  }, [currentIndex, pageState, currentQuestion, sessionId, api]);
}
